import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Modal } from '@/components/ui/Modal'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/Checkbox'
import { Button } from '@/components/ui/button'

const departmentFormSchema = z.object({
  facultyId: z.string().min(1, 'Faculty is required'),
  name: z.string().min(1, 'Department name is required'),
  description: z.string().optional(),
  isActive: z.boolean(),
})

export type DepartmentFormValues = z.infer<typeof departmentFormSchema>

interface DepartmentFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  faculties: Array<{ id: string; name: string }>
  initialValues?: DepartmentFormValues
  submitting?: boolean
  errorMessage?: string | null
  onSubmit: (values: DepartmentFormValues) => Promise<void> | void
  onClose: () => void
}

export const DepartmentFormDialog: React.FC<DepartmentFormDialogProps> = ({
  open,
  mode,
  faculties,
  initialValues,
  submitting = false,
  errorMessage,
  onSubmit,
  onClose,
}) => {
  const defaultValues: DepartmentFormValues = initialValues ?? {
    facultyId: faculties[0]?.id ?? '',
    name: '',
    description: '',
    isActive: true,
  }

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DepartmentFormValues>({
    resolver: zodResolver(departmentFormSchema),
    defaultValues,
  })

  useEffect(() => {
    if (open) {
      reset(
        initialValues ?? {
          facultyId: faculties[0]?.id ?? '',
          name: '',
          description: '',
          isActive: true,
        },
      )
    }
  }, [open, initialValues, faculties, reset])

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={mode === 'create' ? 'Create Department' : 'Edit Department'}
      size="md"
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        {errorMessage && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600" role="alert">
            {errorMessage}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Faculty *
          </label>
          <select
            className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7b2a40]/40 ${
              errors.facultyId ? 'border-red-500' : 'border-secondary-300'
            }`}
            {...register('facultyId')}
          >
            <option value="" disabled>
              Select faculty
            </option>
            {faculties.map((faculty) => (
              <option key={faculty.id} value={faculty.id}>
                {faculty.name}
              </option>
            ))}
          </select>
          {errors.facultyId && (
            <p className="text-xs text-red-500 mt-1">{errors.facultyId.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Department Name *
          </label>
          <Input
            placeholder="Department of Management"
            {...register('name')}
            className={errors.name ? 'border-red-500' : ''}
          />
          {errors.name && (
            <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Description
          </label>
          <textarea
            rows={3}
            className="w-full rounded-md border border-secondary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7b2a40]/40"
            placeholder="Describe the department focus"
            {...register('description')}
          />
        </div>

        <Checkbox label="Department is active" {...register('isActive')} />

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving...' : mode === 'create' ? 'Create Department' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
